import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import { ArticleLabelType } from 'graphql-ts-types';
import type { BackgroundColorProps, BorderRadiusProps } from 'styled-system';
import { backgroundColor, borderRadius } from 'styled-system';

import { useOpenDrawer } from '../../hooks/drawer';
import { useLocalization } from '../../providers/LocaleProvider';
import type { Color } from '../../theme/colors/types';
import type { Space } from '../../theme/system-types';
import type { ArticleLabel } from '../../types/labels';
import { getLabelStringValue } from '../../utils/labels';
import { useAriaLabel } from './hooks';
import type { DiscountLabelsProps } from './index';
import {
  InfoIcon,
  infoIconStyle,
  Label,
  labelStyles,
  smallInfoIconStyle,
  SmallLabel,
} from './label-primitives';
import LabelDrawerContent from './LabelDrawerContent';
import { home24TippTrackingEvent } from './tracking';
import { getColorByType, getDisclaimerContent, isClickable } from './utils';

const LabelWrapper = styled.div<BackgroundColorProps & BorderRadiusProps>`
  ${backgroundColor};
  ${borderRadius};
`;

const BOTTOM_LABEL_PROPS = {
  px: 'sp_4' as Space,
  py: 'sp_0' as Space,
};

const clickableLabelStyles: Partial<ComponentProps<typeof Label>> = {
  border: 'none',
  cursor: 'pointer',
  py: 'sp_0',
};

const enum Event {
  PLPClick = 'product-list-view_click',
  PDPClick = 'product-gallery_click',
}

export interface LabelContentProps extends Pick<
  DiscountLabelsProps,
  'themeBottom' | 'campaigns' | 'isPLP' | 'withBackButton' | 'brand'
> {
  type: ArticleLabel['type'];
  value: ArticleLabel['value'];
  index: number;
  isSmallVariant?: boolean;
}

const getVariantConfig = (isSmallVariant: boolean) =>
  isSmallVariant
    ? { Component: SmallLabel, iconStyle: smallInfoIconStyle }
    : { Component: Label, iconStyle: infoIconStyle };

const LabelContent = ({
  type,
  campaigns,
  value,
  index,
  themeBottom: themeBottomProp,
  isPLP,
  withBackButton,
  brand,
  isSmallVariant = false,
}: LabelContentProps) => {
  const { r, t, fc } = useLocalization();
  const stringValue = getLabelStringValue(value, type, fc);

  const disclaimerContent =
    type === ArticleLabelType.Campaign ? getDisclaimerContent(campaigns, stringValue) : null;

  const isDisclaimerAvailable = Boolean(
    disclaimerContent?.extraDisclaimerContent || disclaimerContent?.legalDisclaimerContent
  );
  const isClickableLabel = isClickable(type, isDisclaimerAvailable);
  const ariaLabel = useAriaLabel(type, isClickableLabel, stringValue);
  const { Component: LabelComponent, iconStyle } = getVariantConfig(isSmallVariant);

  const openDrawer = useOpenDrawer(
    {
      content: (
        <LabelDrawerContent type={type} brand={brand} disclaimerContent={disclaimerContent} />
      ),
      title: type === ArticleLabelType.Home24Selection ? t('h24_tipp_drawer_title') : undefined,
      withBackButton,
      backButtonAriaLabel: r.t('h24_drawer_back_button', {
        backTo: t('h24_add_to_cart_short'),
      }),
    },
    {
      preventDefault: true,
      stopPropagation: true,
    }
  );

  return (
    <LabelWrapper backgroundColor="sys.neutral.background.default" borderRadius="small">
      <LabelComponent
        type={type}
        bg={getColorByType(type, true) as Color}
        data-testid={`discount-label-${index}`}
        aria-label={ariaLabel}
        {...labelStyles}
        {...(themeBottomProp ? { ...BOTTOM_LABEL_PROPS } : {})}
        {...(isClickableLabel
          ? {
              ...clickableLabelStyles,
              as: 'button',
              onClick: openDrawer,
              onClickCapture: () =>
                home24TippTrackingEvent(isPLP ? Event.PLPClick : Event.PDPClick, type),
            }
          : {})}
        isClickable={isClickableLabel}
      >
        {stringValue}
        {isClickableLabel && <InfoIcon {...iconStyle} />}
      </LabelComponent>
    </LabelWrapper>
  );
};

export default LabelContent;
